import { onMounted, onUnmounted, ref, type Ref } from 'vue';

export const isMobile = ref(false);

type MobileChangeCallback = (isMobile: boolean, wasMobile: boolean) => void;
export type UseMobileReturn = {
	isMobile: Ref<boolean>;
	checkIsMobile: () => boolean;
	onMobileChange: (callback: MobileChangeCallback) => () => void;
};

const subscribers = new Set<MobileChangeCallback>();
let isResizeListenerRegistered = false;

export const checkIsMobile = (): boolean => {
	if (typeof window === 'undefined') {
		isMobile.value = false;
		return isMobile.value;
	}

	isMobile.value = window.innerWidth < 769;
	return isMobile.value;
};

const notifySubscribers = (current: boolean, previous: boolean): void => {
	subscribers.forEach((callback) => {
		callback(current, previous);
	});
};

const handleResize = (): void => {
	const wasMobile = isMobile.value;
	const nowMobile = checkIsMobile();

	if (wasMobile === nowMobile) return;

	notifySubscribers(nowMobile, wasMobile);
};

const ensureMobileResizeListener = (): void => {
	if (typeof window === 'undefined' || isResizeListenerRegistered) return;

	checkIsMobile();
	window.addEventListener('resize', handleResize, { passive: true });
	isResizeListenerRegistered = true;
};

export const onMobileChange = (callback: MobileChangeCallback): (() => void) => {
	ensureMobileResizeListener();
	subscribers.add(callback);

	return () => {
		subscribers.delete(callback);
	};
};

/**
 * Composable helper to access mobile state and optionally react to breakpoint changes.
 *
 * @param onChange Optional callback invoked when the mobile breakpoint changes.
 */
export const useMobile = (onChange?: MobileChangeCallback): UseMobileReturn => {
	let stopMobileChangeListener: (() => void) | undefined;

	onMounted(() => {
		checkIsMobile();
		if (!onChange) return;

		stopMobileChangeListener = onMobileChange(onChange);
	});

	onUnmounted(() => {
		if (stopMobileChangeListener) {
			stopMobileChangeListener();
			stopMobileChangeListener = undefined;
		}
	});

	return {
		isMobile,
		checkIsMobile,
		onMobileChange
	};
};
